import { useEffect, useState, useCallback } from "react";

/**
 * Regulens theme hook — single source of truth for light/dark.
 * - Reads/writes localStorage["regulens-theme"].
 * - Sets/reads `data-theme` on <html>.
 * - The initial value is set in public/index.html BEFORE React mounts
 *   (prevents theme flash), so this hook picks it up on first render.
 */
export function useTheme() {
  const [theme, setThemeState] = useState(() => {
    if (typeof document === "undefined") return "light";
    return document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";
  });

  const setTheme = useCallback((next) => {
    const value = next === "dark" ? "dark" : "light";
    setThemeState(value);
    try { localStorage.setItem("regulens-theme", value); } catch (e) {}
    document.documentElement.setAttribute("data-theme", value);
  }, []);

  const toggle = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  // Sync across tabs.
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "regulens-theme" && (e.newValue === "light" || e.newValue === "dark")) {
        setThemeState(e.newValue);
        document.documentElement.setAttribute("data-theme", e.newValue);
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  return { theme, setTheme, toggle };
}
